import { DBSchema, IDBPDatabase, openDB, StoreKey, StoreNames, StoreValue } from 'idb';
import { PageData, SessionData } from '../context/PageDataContext';
import { UserProfile } from './social';

const dbName = 'streamsix_cached_data';

export enum ObjectStore {
    profileDataStoreName = 'public_profile_data',
    pageDataStoreName = 'page_data',
    mostRecentPageStoreName = 'most_recent_page_data',
}

interface CacheSchema extends DBSchema {
    [ObjectStore.profileDataStoreName]: {
        key: string;
        value: UserProfile;
    };
    [ObjectStore.pageDataStoreName]: {
        key: string;
        value: SessionData;
    };
    [ObjectStore.mostRecentPageStoreName]: {
        key: string;
        value: PageData;
    };
}

const dbPromise = (): Promise<IDBPDatabase<CacheSchema>> => {
    return openDB<CacheSchema>(dbName, 2, {
        upgrade(db) {
            const storeList = db.objectStoreNames;
            for (const store of Object.values(ObjectStore)) {
                if (!storeList.contains(store)) {
                    db.createObjectStore(store);
                }
            }
        },
    });
};

type CacheDBNames = StoreNames<CacheSchema>;
type CacheDBKeyType<Name extends CacheDBNames> = StoreKey<CacheSchema, Name>;
type CacheDBValueType<Name extends CacheDBNames> = StoreValue<CacheSchema, Name>;

export async function getItem<Name extends CacheDBNames>(
    storeName: Name,
    key: string,
): Promise<CacheDBValueType<Name> | undefined>;

export async function getItem<Name extends CacheDBNames>(
    storeName: Name,
    key: string,
    defaultValue: CacheDBValueType<Name>,
): Promise<CacheDBValueType<Name>>;

export async function getItem<Name extends CacheDBNames>(
    storeName: Name,
    key: string,
    defaultValue?: CacheDBValueType<Name>,
): Promise<CacheDBValueType<Name> | undefined> {
    const db = await dbPromise();
    const savedData = await db.get(storeName, key);

    return savedData || defaultValue;
}

export const getItems = async <Name extends CacheDBNames>(
    storeName: Name,
    keys: string[],
): Promise<CacheDBValueType<Name>[]> => {
    const db = await dbPromise();
    const tx = db.transaction(storeName);
    const store = tx.objectStore(storeName);

    const items = await Promise.all(keys.map((key) => store.get(key)));
    return items.filter((item) => item !== undefined) as CacheDBValueType<Name>[];
};

/**
 * @param key the cache to used to retrieve the data
 * @param value the data to store
 * @param storeName the cache collection
 * @returns the keys of the stored data
 */
export const setItem = async <Name extends CacheDBNames>(
    key: string,
    value: CacheDBValueType<Name>,
    storeName: Name,
): Promise<CacheDBKeyType<Name>> => {
    const db = await dbPromise();
    return await db.put(storeName, value, key);
};

/**
 *
 * @param entries a object which maps the key and the data store
 * @param storeName the name of the collection in which to store the data
 * @returns the keys of the data stored
 */
export const setItems = async <Name extends CacheDBNames>(
    entries: { key: CacheDBKeyType<Name>; value: CacheDBValueType<Name> }[],
    storeName: Name,
): Promise<CacheDBKeyType<Name>[]> => {
    const db = await dbPromise();
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    const result = await Promise.all(
        entries.map(async ({ key, value }) => await store.put(value, key)),
    );
    await transaction.done;
    return result;
};

export const getPublicProfilesFromCache = async (ids: string[]): Promise<UserProfile[]> => {
    return await getItems(ObjectStore.profileDataStoreName, ids);
};

/**
 * This will save the profiles by both their STREAMSIX ID and Twitch ID
 *
 * TODO: Should we just use an index?
 * I'm not sure how indexes work with for this db so this may require a bit of work when searching
 */
export const savePublicProfilesToCache = async (profiles: UserProfile[]) => {
    await setItems(
        [
            ...profiles.map((profile) => {
                return { key: profile.id, value: profile };
            }),
            ...profiles
                .filter((profile) => profile.twitch_profile !== undefined)
                .map((profile) => {
                    return { key: profile.twitch_profile!.id, value: profile };
                }),
        ],
        ObjectStore.profileDataStoreName,
    );
};

export const getPublicProfileFromCache = async (
    userID: string,
): Promise<UserProfile | undefined> => {
    return await getItem(ObjectStore.profileDataStoreName, userID);
};

/**
 * This will save the public profile the cache indexed by it's STREAMSIX ID and Twitch ID
 * if there is one associated
 */
export const savePublicProfileToCache = async (profile: UserProfile): Promise<string[]> => {
    const result = [await setItem(profile.id, profile, ObjectStore.profileDataStoreName)];
    if (profile.twitch_profile) {
        result.push(
            await setItem(profile.twitch_profile.id, profile, ObjectStore.profileDataStoreName),
        );
    }
    return result;
};
